import { useEffect, useRef, useState } from 'react'
import { Instagram, MapPin, Menu, Phone, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { brand, navItems } from '../data/site'
import { imageSize } from '../data/images'

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const menuRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const closeMenu = () => {
    setMenuOpen(false)
    window.requestAnimationFrame(() => toggleRef.current?.focus())
  }

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => setMenuOpen(false))
    return () => window.cancelAnimationFrame(animationFrame)
  }, [location.pathname])

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen)
    if (!menuOpen) return

    const menu = menuRef.current
    const focusable = menu?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
    focusable?.[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu()
        return
      }
      if (event.key !== 'Tab' || !focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.classList.remove('menu-open')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  return (
    <header className="site-header">
      <a className="skip-link" href="#main-content">Skip to content</a>

      <div className="utility-bar">
        <div className="container utility-bar__inner">
          <a href={brand.mapUrl} target="_blank" rel="noopener noreferrer">
            <MapPin aria-hidden="true" /> {brand.addressLine1}, {brand.addressLine2}
          </a>
          <span aria-hidden="true" />
          <a href={brand.phoneHref}><Phone aria-hidden="true" /> {brand.phoneDisplay}</a>
          <a href={brand.instagram} target="_blank" rel="noopener noreferrer" aria-label="Diamond Suites Ocala on Instagram">
            <Instagram aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="nav-shell">
        <div className="container nav-shell__inner">
          <Link className="brand" to="/" aria-label="Diamond Suites Ocala home">
            <img src={`${import.meta.env.BASE_URL}images/DiamondSuitesOcalaLogo.gif`} alt="Diamond Suites Ocala" {...imageSize('DiamondSuitesOcalaLogo.gif')} />
          </Link>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Link className="button button--small header-cta" to="/contact/">
            Join the Diamond Family
          </Link>

          <button
            ref={toggleRef}
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      {menuOpen && <button className="menu-backdrop" type="button" aria-hidden="true" tabIndex={-1} onClick={closeMenu} />}
      <div ref={menuRef} id="mobile-navigation" className={`mobile-menu ${menuOpen ? 'is-open' : ''}`}>
        <button className="mobile-menu__close" type="button" onClick={closeMenu} aria-label="Close menu">
          <X aria-hidden="true" />
        </button>
        <p className="eyebrow">Explore</p>
        <nav aria-label="Mobile navigation">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}>
              {item.label}
            </NavLink>
          ))}
          <Link to="/salon-etiquette/">Salon Etiquette</Link>
        </nav>
        <div className="mobile-menu__contact">
          <a href={brand.phoneHref}>{brand.phoneDisplay}</a>
          <a href={`mailto:${brand.email}`}>{brand.email}</a>
        </div>
      </div>
    </header>
  )
}
